import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { LogoFull } from "@/components/Logo";
import { api, authHeader, imgSrc } from "@/lib/api";
import { CATEGORIES } from "@/constants/site";

const EMPTY_LISTING = {
  title: "", category: "engins", description: "", price: 0,
  price_unit: "€ / jour", image_url: "", available: true, specs: "",
};

const QUOTE_STATUSES = ["nouveau", "en cours", "traité"];
const RESA_STATUSES = ["en attente", "confirmée", "terminée", "annulée"];

const inputCls =
  "w-full bg-[#0D1117] border border-[#21262D] rounded-xl px-4 py-3 text-sm text-[#F0F6FC] placeholder:text-[#8B949E]/60 focus:outline-none focus:border-[#F2541B] transition-colors duration-300";

const catLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;

const fmtDate = (d) => (d ? new Date(d.includes("T") ? d : d + "T00:00:00").toLocaleDateString("fr-FR") : "—");

function ListingForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/upload", data, {
        headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: res.data.path }));
      toast.success("Photo envoyée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi de la photo impossible");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      className="bg-[#161B22] border border-[#F2541B]/40 rounded-2xl p-6 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
      data-testid="listing-form"
    >
      <input required placeholder="Titre *" value={form.title} onChange={set("title")} className={inputCls} data-testid="listing-form-title" />
      <select value={form.category} onChange={set("category")} className={inputCls} data-testid="listing-form-category">
        {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <textarea placeholder="Description" rows={2} value={form.description} onChange={set("description")} className={`${inputCls} sm:col-span-2 resize-none`} data-testid="listing-form-description" />
      <input type="number" min="0" step="0.01" placeholder="Prix (0 = sur devis)" value={form.price} onChange={set("price")} className={inputCls} data-testid="listing-form-price" />
      <input placeholder="Unité (ex : € / jour)" value={form.price_unit} onChange={set("price_unit")} className={inputCls} data-testid="listing-form-unit" />

      <div className="sm:col-span-2 flex flex-wrap items-center gap-4 bg-[#0D1117] border border-dashed border-[#21262D] rounded-xl p-4" data-testid="listing-form-photo-zone">
        {form.image_url ? (
          <img src={imgSrc(form.image_url)} alt="Aperçu" className="w-24 h-24 rounded-lg object-cover border border-[#21262D]" data-testid="listing-form-photo-preview" />
        ) : (
          <div className="w-24 h-24 rounded-lg border border-[#21262D] flex items-center justify-center text-[#8B949E]">
            <ImagePlus size={22} />
          </div>
        )}
        <div className="flex-1 min-w-[200px]">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadPhoto} className="hidden" data-testid="listing-form-file" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="listing-form-upload-btn"
            className="bg-[#F2541B] hover:bg-[#D9420B] disabled:opacity-60 text-white px-5 py-2.5 rounded-full text-sm font-display font-bold uppercase"
          >
            {uploading ? "Envoi en cours…" : form.image_url ? "Changer la photo" : "Envoyer une photo"}
          </button>
          <p className="font-mono text-[10px] text-[#8B949E] mt-2">JPG, PNG, WEBP ou GIF — 10 Mo max</p>
        </div>
      </div>

      <input placeholder="Ou collez une URL d'image" value={form.image_url} onChange={set("image_url")} className={`${inputCls} sm:col-span-2`} data-testid="listing-form-image" />
      <input placeholder="Caractéristiques (séparées par des virgules)" value={form.specs} onChange={set("specs")} className={`${inputCls} sm:col-span-2`} data-testid="listing-form-specs" />
      <label className="flex items-center gap-3 text-sm text-[#F0F6FC]">
        <input type="checkbox" checked={form.available} onChange={set("available")} className="w-4 h-4 accent-[#F2541B]" data-testid="listing-form-available" />
        Disponible à la réservation
      </label>
      <div className="flex gap-3 sm:justify-end">
        <button type="button" onClick={onCancel} data-testid="listing-form-cancel" className="border border-[#21262D] text-[#8B949E] px-5 py-2.5 rounded-full text-sm font-display font-bold uppercase">
          Annuler
        </button>
        <button type="submit" data-testid="listing-form-save" className="bg-[#F2541B] hover:bg-[#D9420B] text-white px-5 py-2.5 rounded-full text-sm font-display font-bold uppercase">
          Enregistrer
        </button>
      </div>
    </form>
  );
}

export default function AdminPanel() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("annonces");
  const [listings, setListings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const loadListings = useCallback(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
  }, []);

  const loadQuotes = useCallback(() => {
    api.get("/quotes", authHeader()).then((r) => setQuotes(r.data)).catch(() => {});
  }, []);

  const loadReservations = useCallback(() => {
    api.get("/reservations", authHeader()).then((r) => setReservations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me", authHeader())
      .then((r) => { setUser(r.data); loadListings(); loadQuotes(); loadReservations(); })
      .catch(() => navigate("/admin/login"));
  }, [navigate, loadListings, loadQuotes, loadReservations]);

  const logout = () => { localStorage.removeItem("tplm_token"); navigate("/admin/login"); };

  const saveListing = async (form) => {
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      specs: form.specs ? form.specs.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (editing === "new") {
        await api.post("/listings", payload, authHeader());
        toast.success("Annonce créée");
      } else {
        await api.put(`/listings/${editing.id}`, payload, authHeader());
        toast.success("Annonce mise à jour");
      }
      setEditing(null);
      loadListings();
    } catch {
      toast.error("Enregistrement impossible");
    }
  };

  const removeListing = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await api.delete(`/listings/${id}`, authHeader());
      toast.success("Annonce supprimée");
      loadListings();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const toggleAvailable = async (l) => {
    try {
      await api.put(`/listings/${l.id}`, {
        title: l.title, category: l.category, description: l.description,
        price: l.price, price_unit: l.price_unit, image_url: l.image_url,
        available: !l.available, specs: l.specs || [],
      }, authHeader());
      loadListings();
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  const setQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status }, authHeader());
      loadQuotes();
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  const setResaStatus = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}`, { status }, authHeader());
      loadReservations();
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  if (!user) {
    return <div className="min-h-screen bg-[#0D1117] flex items-center justify-center text-[#8B949E] font-mono text-sm" data-testid="admin-loading">Chargement…</div>;
  }

  const TABS = [
    ["annonces", `Annonces (${listings.length})`],
    ["reservations", `Réservations (${reservations.length})`],
    ["demandes", `Demandes (${quotes.length})`],
  ];

  return (
    <div className="min-h-screen bg-[#0D1117] text-[#F0F6FC]" data-testid="admin-panel">
      <header className="border-b border-[#21262D] bg-[#0D1117]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <LogoFull size={30} />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-xs text-[#8B949E]" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-[#8B949E] hover:text-[#F2541B] transition-colors text-sm">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`font-display font-bold uppercase text-sm tracking-wide px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                tab === id ? "bg-[#F2541B] border-[#F2541B] text-white" : "border-[#21262D] text-[#8B949E] hover:text-[#F0F6FC]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "annonces" && (
          <div data-testid="admin-listings-section">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display font-bold uppercase text-xl">Catalogue</h2>
              {editing === null && (
                <button
                  onClick={() => setEditing("new")}
                  data-testid="listing-add-btn"
                  className="flex items-center gap-2 bg-[#F2541B] hover:bg-[#D9420B] text-white px-5 py-2.5 rounded-full text-sm font-display font-bold uppercase"
                >
                  <Plus size={16} /> Nouvelle annonce
                </button>
              )}
            </div>

            {editing !== null && (
              <ListingForm
                initial={editing === "new" ? EMPTY_LISTING : { ...editing, specs: (editing.specs || []).join(", ") }}
                onSave={saveListing}
                onCancel={() => setEditing(null)}
              />
            )}

            <div className="flex flex-col gap-3">
              {listings.map((l) => (
                <div
                  key={l.id}
                  data-testid={`admin-listing-row-${l.id}`}
                  className="bg-[#161B22] border border-[#21262D] rounded-2xl p-4 flex flex-wrap items-center gap-4"
                >
                  {l.image_url && (
                    <img src={imgSrc(l.image_url)} alt="" className="w-16 h-16 rounded-xl object-cover border border-[#21262D]" />
                  )}
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-display font-bold uppercase tracking-tight">{l.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#E5A93C] mt-1">{catLabel(l.category)}</p>
                  </div>
                  <span className="font-mono text-sm text-[#F2541B]">{l.price > 0 ? `${l.price} ${l.price_unit}` : l.price_unit}</span>
                  <button
                    onClick={() => toggleAvailable(l)}
                    data-testid={`listing-toggle-${l.id}`}
                    className={`font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border ${
                      l.available ? "border-[#2D5A40] text-[#7BD8A4] bg-[#1B3B2B]/40" : "border-[#5A2D2D] text-[#E5A93C] bg-[#3B1B1B]/40"
                    }`}
                  >
                    {l.available ? "Disponible" : "Sur demande"}
                  </button>
                  <button onClick={() => setEditing(l)} data-testid={`listing-edit-${l.id}`} className="text-[#8B949E] hover:text-[#F0F6FC] transition-colors" aria-label="Modifier">
                    <Pencil size={17} />
                  </button>
                  <button onClick={() => removeListing(l.id)} data-testid={`listing-delete-${l.id}`} className="text-[#8B949E] hover:text-[#F2541B] transition-colors" aria-label="Supprimer">
                    <Trash2 size={17} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reservations" && (
          <div data-testid="admin-reservations-section">
            <h2 className="font-display font-bold uppercase text-xl mb-6">Réservations en ligne</h2>
            {reservations.length === 0 ? (
              <p className="text-[#8B949E] py-12 text-center" data-testid="reservations-empty">Aucune réservation pour le moment.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {reservations.map((r) => (
                  <div key={r.id} data-testid={`admin-resa-row-${r.id}`} className="bg-[#161B22] border border-[#21262D] rounded-2xl p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-mono font-bold text-[#F2541B] tracking-widest" data-testid={`admin-resa-ref-${r.id}`}>{r.reference}</p>
                        <p className="font-display font-bold uppercase tracking-tight text-lg mt-1">{r.name}</p>
                        <p className="font-mono text-xs text-[#8B949E] mt-1">
                          {r.phone}{r.email ? ` · ${r.email}` : ""}
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-[#E5A93C] mt-2">
                          {r.listing_title} — du {fmtDate(r.date_start)}{r.date_end ? ` au ${fmtDate(r.date_end)}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] text-[#8B949E]">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : ""}
                        </span>
                        <select
                          value={r.status}
                          onChange={(e) => setResaStatus(r.id, e.target.value)}
                          data-testid={`resa-status-${r.id}`}
                          className="bg-[#0D1117] border border-[#21262D] rounded-lg px-3 py-1.5 font-mono text-xs text-[#F0F6FC] focus:outline-none focus:border-[#F2541B]"
                        >
                          {RESA_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    {r.message && <p className="text-sm text-[#8B949E] mt-3 leading-relaxed">{r.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "demandes" && (
          <div data-testid="admin-quotes-section">
            <h2 className="font-display font-bold uppercase text-xl mb-6">Demandes de devis</h2>
            {quotes.length === 0 ? (
              <p className="text-[#8B949E] py-12 text-center" data-testid="quotes-empty">Aucune demande pour le moment.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {quotes.map((q) => (
                  <div key={q.id} data-testid={`admin-quote-row-${q.id}`} className="bg-[#161B22] border border-[#21262D] rounded-2xl p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-display font-bold uppercase tracking-tight text-lg">{q.name}</p>
                        <p className="font-mono text-xs text-[#8B949E] mt-1">
                          {q.phone}{q.email ? ` · ${q.email}` : ""}
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-[#E5A93C] mt-2">
                          {catLabel(q.service)}{q.listing_title ? ` — ${q.listing_title}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] text-[#8B949E]">
                          {q.created_at ? new Date(q.created_at).toLocaleDateString("fr-FR") : ""}
                        </span>
                        <select
                          value={q.status}
                          onChange={(e) => setQuoteStatus(q.id, e.target.value)}
                          data-testid={`quote-status-${q.id}`}
                          className="bg-[#0D1117] border border-[#21262D] rounded-lg px-3 py-1.5 font-mono text-xs text-[#F0F6FC] focus:outline-none focus:border-[#F2541B]"
                        >
                          {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    {q.message && <p className="text-sm text-[#8B949E] mt-3 leading-relaxed">{q.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
